<template>
  <div class="page">
    <div v-if="loading" class="loading">Cargando...</div>
    <div v-else-if="!response" class="loading error">Intento no encontrado</div>
    <div v-else>
      <header class="header">
        <h2>Autodiagnóstico SAC</h2>
        <div class="actions">
          <button class="btn btn-outline" :disabled="saving" @click="saveDraft(false)">
            Guardar progreso
          </button>
          <button class="btn btn-primary" :disabled="completing" @click="confirmComplete = true">
            {{ completing ? 'Completando…' : 'Completar' }}
          </button>
        </div>
      </header>

      <nav class="tabs" v-if="catalog.rubros && catalog.rubros.length">
        <button
          v-for="rubro in catalog.rubros"
          :key="rubro._id"
          class="tab"
          :class="{ active: activeTab === rubro._id }"
          @click="activeTab = rubro._id"
        >
          {{ rubro.name }}
          <span v-if="rubro.isProfile" class="chip chip-grey">P</span>
        </button>
      </nav>

      <section
        class="tab-panel"
        v-for="rubro in catalog.rubros"
        :key="rubro._id"
        :id="'rubro-' + rubro._id"
        v-show="activeTab === rubro._id"
      >
        <div
          v-for="section in (rubro.sections || [])"
          :key="section._id"
          class="card"
        >
          <div class="card-title">
            <span>{{ section.name }}</span>
             <span class="chip chip-grey">{{ section.isProfile ? 'Perfil' : `peso ${section.weight}` }}</span>
          </div>
          <div class="card-body">
            <SacResponseAnswer
              v-for="q in (section.questions || [])"
              :key="q._id"
              :ref="'q-' + q._id"
              :question="q"
              :answer="answerMap[q._id]"
              :unanswered="missingIds.includes(q._id)"
              @change="onChange"
            />
          </div>
        </div>
        <div v-if="getNextRubro(rubro)" class="rubro-actions">
          <button
            type="button"
            class="btn btn-primary"
            @click="continueToRubro(rubro)"
          >
            Continuar
          </button>
        </div>
      </section>
    </div>

    <div v-if="confirmComplete" class="modal-backdrop" @click.self="confirmComplete = false">
      <div class="modal" role="dialog" aria-modal="true">
        <div class="modal-header">Completar autodiagnóstico</div>
        <div class="modal-body">
          ¿Completar el intento? No podrás modificar las respuestas después.
        </div>
        <div class="modal-actions">
          <button class="btn btn-outline" :disabled="completing" @click="confirmComplete = false">Cancelar</button>
          <button class="btn btn-primary" :disabled="completing" @click="confirmComplete = false; complete()">
            {{ completing ? 'Completando…' : 'Sí, completar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import { getCatalog, getResponse, upsertAnswers, completeResponse } from '../../api/sac';
import SacResponseAnswer from '../../components/sac/SacResponseAnswer.vue';

export default {
  name: 'SacMemberForm',
  components: { SacResponseAnswer },
  data() {
    return {
      loading: true,
      response: null,
      catalog: { rubros: [] },
      answerMap: {},
      activeTab: null,
          saving: false,
          completing: false,
          confirmComplete: false,
          missingIds: [],
        };
  },
  async mounted() {
    await this.load();
    this._saveTimer = null;
  },
  beforeDestroy() {
    if (this._saveTimer) clearTimeout(this._saveTimer);
  },
  methods: {
    ...mapMutations('notification', ['show']),
    async load() {
      this.loading = true;
      try {
        const [cat, resp] = await Promise.all([
          getCatalog(),
          getResponse(this.$route.params.id),
        ]);
        this.catalog = cat;
        this.response = resp.response;
        const map = {};
        for (const a of resp.answers || []) {
          map[a.question?._id || a.question] = a;
        }
        this.answerMap = map;
        if (cat.rubros?.length) this.activeTab = cat.rubros[0]._id;
        if (this.response.status !== 'IN_PROGRESS') {
          this.$router.push(`/sac/responses/${this.response._id}/result`);
        }
      } catch (e) {
        this.show({
          text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo cargar el autodiagnóstico',
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    onChange(payload) {
      this.$set(this.answerMap, payload.questionId, {
        ...this.answerMap[payload.questionId],
        ...payload,
      });
      this.clearMissing(payload.questionId);
      if (this._saveTimer) clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => this.saveDraft(true), 1500);
    },
    collectAnswers() {
      return Object.values(this.answerMap)
        .filter((a) => a.selectedOptionId || (a.selectedOptions && a.selectedOptions.length) || a.valueRaw != null)
        .map((a) => {
          const qid = a.questionId || (a.question && (a.question._id || a.question.id)) || a.question;
          return {
            questionId: qid,
            selectedOptionId: a.selectedOptionId,
            selectedOptions: a.selectedOptions,
            valueRaw: a.valueRaw,
            comment: a.comment,
          };
        });
    },
    isAnswered(question) {
      const a = this.answerMap[question._id];
      if (!a) return false;
      if (a.selectedOptionId) return true;
      if (a.selectedOption && (a.selectedOption._id || a.selectedOption.id)) return true;
      if (a.selectedOptions && a.selectedOptions.length) return true;
      if (a.valueRaw !== null && a.valueRaw !== undefined && a.valueRaw !== '') return true;
      return false;
    },
    findMissing() {
      const missing = [];
      for (const rubro of this.catalog.rubros || []) {
        for (const section of (rubro.sections || [])) {
          for (const q of (section.questions || [])) {
            if (q.isRequired && !this.isAnswered(q)) missing.push(q._id);
          }
        }
      }
      return missing;
    },
    locateQuestion(qid) {
      for (const rubro of this.catalog.rubros || []) {
        for (const section of (rubro.sections || [])) {
          for (const q of (section.questions || [])) {
            if (q._id === qid) return { rubro, section, question: q };
          }
        }
      }
      return null;
    },
    getNextRubro(rubro) {
      const rubros = this.catalog.rubros || [];
      const rubroIndex = rubros.findIndex((item) => item._id === rubro._id);
      for (let index = rubroIndex + 1; index < rubros.length; index += 1) {
        if ((rubros[index].sections || []).length) return rubros[index];
      }

      return null;
    },
    continueToRubro(rubro) {
      const next = this.getNextRubro(rubro);
      if (!next) return;

      this.activeTab = next._id;
      this.$nextTick(() => {
        const element = document.getElementById(`rubro-${next._id}`);
        if (element && element.scrollIntoView) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    validateAndScroll() {
      const missing = this.findMissing();
      this.missingIds = missing;
      if (missing.length === 0) return true;
      const first = this.locateQuestion(missing[0]);
      if (first) this.activeTab = first.rubro._id;
      this.show({ text: 'Faltan preguntas por responder', color: 'error' });
      this.$nextTick(() => {
        const ref = this.$refs['q-' + missing[0]];
        const el = ref && ref.$el ? ref.$el : (ref && ref[0] && ref[0].$el ? ref[0].$el : null);
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      return false;
    },
    clearMissing(qid) {
      if (this.missingIds.includes(qid)) {
        this.missingIds = this.missingIds.filter((id) => id !== qid);
      }
    },
    async saveDraft(silent = false) {
      this.saving = true;
      try {
        await upsertAnswers(this.response._id, this.collectAnswers());
        if (!silent) {
          this.show({ text: 'Progreso guardado correctamente', color: 'success' });
        }
        return true;
      } catch (e) {
        this.show({
          text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo guardar el progreso',
          color: 'error',
        });
        return false;
      } finally {
        this.saving = false;
      }
    },
    async complete() {
      if (!this.validateAndScroll()) return;
      this.completing = true;
      if (this._saveTimer) {
        clearTimeout(this._saveTimer);
        this._saveTimer = null;
      }
      try {
        const saved = await this.saveDraft(true);
        if (!saved) return;
        await completeResponse(this.response._id);
        this.show({ text: 'Autodiagnóstico completado correctamente', color: 'success' });
        this.$router.push(`/sac/responses/${this.response._id}/result`);
      } catch (e) {
        this.show({
          text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo completar el autodiagnóstico',
          color: 'error',
        });
      } finally {
        this.completing = false;
      }
    },
  },
};
</script>

<style scoped>
.page {
  padding: 16px;
  max-width: 1100px;
  margin: 0 auto;
}
.loading {
  padding: 32px;
  text-align: center;
  color: #555;
}
.loading.error {
  color: #b00020;
  background: #fdecea;
  border: 1px solid #f5c2c0;
  border-radius: 8px;
}
.header {
  position: sticky;
  top: var(--layout-topbar-height, 0px);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 0;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
}
.header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
}
.actions {
  display: flex;
  gap: 8px;
}
.btn {
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: background 0.15s, border 0.15s, color 0.15s;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-outline {
  background: #fff;
  border-color: #c4c4c4;
  color: #333;
}
.btn-outline:hover:not(:disabled) {
  border-color: #1976d2;
  color: #1976d2;
}
.btn-primary {
  background: #1976d2;
  color: #fff;
  border-color: #1976d2;
}
.btn-primary:hover:not(:disabled) {
  background: #1565c0;
}
.tabs {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 16px;
  overflow-x: auto;
  position: sticky;
  top: 64px;
  background: #fff;
  z-index: 5;
  padding-top: 4px;
}
.tab {
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  color: #555;
  white-space: nowrap;
  margin-bottom: -2px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 0.15s, border-color 0.15s;
}
.tab:hover {
  color: #1976d2;
}
.tab.active {
  color: #1976d2;
  border-bottom-color: #1976d2;
}
.tab-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}
.card-title {
  padding: 12px 16px;
  font-weight: 600;
  font-size: 1rem;
  background: #fafafa;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 8px;
}
.card-title span:first-child {
  flex: 1;
}
.card-body {
  padding: 8px 16px 16px;
}
.rubro-actions {
  display: flex;
  justify-content: flex-end;
  padding: 0 16px 16px;
}
.chip {
  display: inline-block;
  padding: 2px 8px;
  font-size: 0.75rem;
  border-radius: 10px;
  font-weight: 500;
  line-height: 1.4;
}
.chip-grey {
  background: #eceff1;
  color: #37474f;
}
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}
.modal {
  background: #fff;
  border-radius: 8px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: modal-in 0.15s ease-out;
}
@keyframes modal-in {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}
.modal-header {
  padding: 14px 16px;
  font-weight: 600;
  font-size: 1rem;
  border-bottom: 1px solid #eee;
  color: #1f2937;
}
.modal-body {
  padding: 16px;
  font-size: 0.95rem;
  color: #444;
  line-height: 1.4;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  background: #fafafa;
  border-top: 1px solid #eee;
}
</style>
